/**
 * Vanilla white-label Privacy Dashboard mount for org websites.
 * Never rendered inside Aithoris admin — org embeds via SDK.
 */
import type { ConsentSdkClient } from "./consent-sdk-client";
import { readPrivacyToken } from "./consent-sdk-client";

export type PrivacyDashboardTheme = {
  logoUrl?: string;
  primaryColor?: string;
  fontFamily?: string;
  borderRadius?: string;
  theme?: "light" | "dark";
  buttonStyle?: "solid" | "outline" | "soft";
  density?: "comfortable" | "compact";
  language?: string;
};

export type MountPrivacyDashboardOptions = {
  container: HTMLElement;
  principalId?: string;
  identifierType?: string;
  /** @deprecated Prefer theme.primaryColor */
  primaryColor?: string;
  theme?: PrivacyDashboardTheme;
  onClose?: () => void;
};

type SectionId =
  | "overview"
  | "consent"
  | "history"
  | "activity"
  | "requests"
  | "notifications"
  | "preferences"
  | "actions";

export function mountPrivacyDashboard(
  client: ConsentSdkClient,
  options: MountPrivacyDashboardOptions,
): { unmount: () => void; refresh: () => Promise<void> } {
  const root = options.container;
  const theme: PrivacyDashboardTheme = {
    primaryColor: options.theme?.primaryColor || options.primaryColor || "#0f766e",
    fontFamily: options.theme?.fontFamily || "ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif",
    borderRadius: options.theme?.borderRadius || "14px",
    theme: options.theme?.theme || "light",
    buttonStyle: options.theme?.buttonStyle || "solid",
    density: options.theme?.density || "comfortable",
    logoUrl: options.theme?.logoUrl,
    language: options.theme?.language || "en",
  };
  const color = theme.primaryColor!;
  const dark = theme.theme === "dark";
  const pad = theme.density === "compact" ? "12px" : "18px";
  const radius = theme.borderRadius!;
  let principalId = options.principalId || "";
  let identifierType = options.identifierType || "email";
  let destroyed = false;
  let activeSection: SectionId = "overview";

  const esc = (s: string) =>
    String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const btnBg =
    theme.buttonStyle === "outline"
      ? "transparent"
      : theme.buttonStyle === "soft"
        ? `color-mix(in srgb, ${color} 14%, transparent)`
        : color;
  const btnColor =
    theme.buttonStyle === "solid" ? "#fff" : color;
  const btnBorder =
    theme.buttonStyle === "outline" ? `1px solid ${color}` : "0";

  const surface = dark ? "#111827" : "#ffffff";
  const ink = dark ? "#f8fafc" : "#0f172a";
  const muted = dark ? "#94a3b8" : "#64748b";
  const line = dark ? "rgba(148,163,184,.2)" : "#e2e8f0";
  const pageBg = dark
    ? "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(15,118,110,.25), transparent), #0b1220"
    : "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(15,118,110,.08), transparent), #f4f7fb";

  const shell = () => {
    root.innerHTML = `
      <div class="cpd-root" style="font-family:${esc(theme.fontFamily!)};color:${ink};max-width:960px;margin:0 auto;">
        <style>
          .cpd-root *{box-sizing:border-box}
          .cpd-page{background:${pageBg};border-radius:${radius};padding:${pad};border:1px solid ${line}}
          .cpd-card{border:1px solid ${line};border-radius:${radius};padding:${pad};margin-bottom:12px;background:${surface};box-shadow:0 1px 2px rgba(15,23,42,.04),0 10px 28px -18px rgba(15,23,42,.18)}
          .cpd-nav{display:flex;flex-wrap:wrap;gap:8px;margin:12px 0}
          .cpd-nav button{border:1px solid ${line};background:${dark ? "#1f2937" : "#f8fafc"};border-radius:999px;padding:8px 14px;cursor:pointer;font-size:12px;font-weight:600;color:${muted}}
          .cpd-nav button.is-active{background:${color};color:#fff;border-color:${color}}
          .cpd-btn{background:${btnBg};color:${btnColor};border:${btnBorder};border-radius:10px;padding:10px 14px;cursor:pointer;font-weight:650}
          .cpd-btn.ghost{background:${surface};color:${ink};border:1px solid ${line}}
          .cpd-btn.danger{background:#dc2626;color:#fff;border:0}
          .cpd-input{width:100%;padding:10px 12px;border:1px solid ${line};border-radius:10px;margin:6px 0 12px;background:${dark ? "#0b1220" : "#fff"};color:${ink}}
          .cpd-muted{color:${muted};font-size:13px}
          .cpd-row{display:flex;justify-content:space-between;gap:12px;padding:10px 0;border-bottom:1px solid ${line};align-items:center}
          .cpd-row:last-child{border-bottom:0}
          .cpd-actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:12px}
          .cpd-error{color:#b91c1c;font-size:13px;margin:8px 0}
          .cpd-toggle{width:44px;height:26px;border-radius:999px;border:0;position:relative;cursor:pointer;background:${line}}
          .cpd-toggle.on{background:${color}}
          .cpd-toggle span{position:absolute;top:3px;left:3px;width:20px;height:20px;border-radius:999px;background:#fff;transition:left .15s}
          .cpd-toggle.on span{left:21px}
          .cpd-grid{display:grid;gap:12px}
          @media(min-width:720px){.cpd-grid-2{grid-template-columns:1fr 1fr}}
          .cpd-pill{display:inline-flex;align-items:center;border-radius:999px;padding:2px 10px;font-size:10px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;background:color-mix(in srgb, ${color} 14%, transparent);color:${color}}
          .cpd-danger-zone{border:1px solid color-mix(in srgb,#dc2626 35%,${line});background:color-mix(in srgb,#dc2626 6%,${surface});border-radius:${radius};padding:${pad};margin-top:12px}
        </style>
        <div class="cpd-page">
          <div class="cpd-card" style="margin:0;display:flex;justify-content:space-between;align-items:center;gap:12px">
            <div style="display:flex;gap:12px;align-items:center">
              ${
                theme.logoUrl
                  ? `<img src="${esc(theme.logoUrl)}" alt="" style="height:36px;width:auto;border-radius:8px" />`
                  : `<div style="width:36px;height:36px;border-radius:10px;background:color-mix(in srgb, ${color} 16%, transparent);color:${color};display:grid;place-items:center;font-weight:800">P</div>`
              }
              <div>
                <div style="font-size:11px;font-weight:700;color:${color};text-transform:uppercase;letter-spacing:.05em">Privacy &amp; Consent</div>
                <h2 style="margin:2px 0 0;font-size:22px;letter-spacing:-.02em">Privacy Dashboard</h2>
                <p class="cpd-muted" style="margin:2px 0 0">Manage preferences, history, and privacy requests on this site.</p>
              </div>
            </div>
            ${options.onClose ? `<button type="button" class="cpd-btn ghost" data-cpd-close>Close</button>` : ""}
          </div>
          <div class="cpd-nav" data-cpd-nav></div>
          <div data-cpd-body></div>
          <div class="cpd-error" data-cpd-error hidden></div>
        </div>
      </div>`;
    root.querySelector("[data-cpd-close]")?.addEventListener("click", () => options.onClose?.());
  };

  const setError = (msg: string | null) => {
    const el = root.querySelector("[data-cpd-error]") as HTMLElement | null;
    if (!el) return;
    if (!msg) {
      el.hidden = true;
      el.textContent = "";
      return;
    }
    el.hidden = false;
    el.textContent = msg;
  };

  const renderNav = () => {
    const nav = root.querySelector("[data-cpd-nav]");
    if (!nav) return;
    const sections: Array<{ id: SectionId; label: string }> = [
      { id: "overview", label: "Overview" },
      { id: "preferences", label: "My Consents" },
      { id: "history", label: "Consent History" },
      { id: "requests", label: "My Requests" },
      { id: "notifications", label: "Notifications" },
      { id: "activity", label: "Activity" },
      { id: "consent", label: "Current Status" },
      { id: "actions", label: "Raise a Request" },
    ];
    nav.innerHTML = sections
      .map(
        (s) =>
          `<button type="button" data-section="${s.id}" class="${activeSection === s.id ? "is-active" : ""}">${s.label}</button>`,
      )
      .join("");
    nav.querySelectorAll("button").forEach((btn) => {
      btn.addEventListener("click", () => {
        activeSection = (btn.getAttribute("data-section") || "overview") as SectionId;
        void refresh();
      });
    });
  };

  const renderLogin = () => {
    const body = root.querySelector("[data-cpd-body]");
    if (!body) return;
    body.innerHTML = `
      <div class="cpd-card">
        <span class="cpd-pill">Secure access</span>
        <h3 style="margin:10px 0 4px;font-size:18px">Sign in to your privacy space</h3>
        <p class="cpd-muted">Use the identifier your organisation knows you by. Aithoris stays in the background.</p>
        <label class="cpd-muted">Identifier type</label>
        <select class="cpd-input" data-cpd-id-type>
          <option value="email"${identifierType === "email" ? " selected" : ""}>Email</option>
          <option value="phone"${identifierType === "phone" ? " selected" : ""}>Phone</option>
          <option value="customer_id"${identifierType === "customer_id" ? " selected" : ""}>Customer ID</option>
          <option value="account_id"${identifierType === "account_id" ? " selected" : ""}>Account ID</option>
        </select>
        <label class="cpd-muted">Principal ID</label>
        <input class="cpd-input" data-cpd-principal placeholder="you@example.com" value="${esc(principalId)}" />
        <div class="cpd-actions">
          <button type="button" class="cpd-btn" data-cpd-otp>Send OTP</button>
        </div>
        <label class="cpd-muted">OTP</label>
        <input class="cpd-input" data-cpd-otp-val placeholder="Enter OTP" />
        <div class="cpd-actions">
          <button type="button" class="cpd-btn" data-cpd-login>Access my dashboard</button>
        </div>
      </div>`;
    body.querySelector("[data-cpd-otp]")?.addEventListener("click", async () => {
      setError(null);
      principalId = (body.querySelector("[data-cpd-principal]") as HTMLInputElement).value.trim();
      identifierType = (body.querySelector("[data-cpd-id-type]") as HTMLSelectElement).value;
      try {
        const res = (await client.requestOtp(principalId, identifierType)) as { demoOtpHint?: string };
        setError(
          res.demoOtpHint
            ? `OTP sent (demo mode). Use code: ${res.demoOtpHint}`
            : "OTP sent. Check your channel.",
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : "OTP failed");
      }
    });
    body.querySelector("[data-cpd-login]")?.addEventListener("click", async () => {
      setError(null);
      principalId = (body.querySelector("[data-cpd-principal]") as HTMLInputElement).value.trim();
      identifierType = (body.querySelector("[data-cpd-id-type]") as HTMLSelectElement).value;
      const otp = (body.querySelector("[data-cpd-otp-val]") as HTMLInputElement).value.trim();
      try {
        await client.loginWithOtp(principalId, otp, identifierType);
        await refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Login failed");
      }
    });
  };

  const refresh = async () => {
    if (destroyed) return;
    shell();
    renderNav();
    if (!readPrivacyToken(client.appId)) {
      renderLogin();
      return;
    }
    const body = root.querySelector("[data-cpd-body]");
    if (!body) return;
    body.innerHTML = `<div class="cpd-card"><p class="cpd-muted">Loading…</p></div>`;
    try {
      if (activeSection === "overview" || activeSection === "consent" || activeSection === "preferences") {
        const dash = (await client.getPrivacyDashboard()) as {
          policy: {
            policyId: string;
            version: string;
            title: string | null;
            notice: string | null;
            purposes: Array<{ id: string; name: string; description?: string; required?: boolean }>;
          } | null;
          consent: {
            status: string;
            policyVersion: string;
            lastUpdated: string;
            purposes: Array<{ data_point_id: string; consent_granted: boolean }>;
          } | null;
          openRequestCount: number;
          unreadNotificationCount: number;
        };

        if (activeSection === "overview") {
          const granted = (dash.consent?.purposes ?? []).filter((p) => p.consent_granted).length;
          const total = (dash.consent?.purposes ?? []).length || 1;
          const score = Math.round((granted / total) * 100);
          body.innerHTML = `
            <div class="cpd-grid cpd-grid-2">
              <div class="cpd-card">
                <span class="cpd-pill">Overview</span>
                <div class="cpd-row"><span>Consent status</span><strong>${esc(dash.consent?.status || "None")}</strong></div>
                <div class="cpd-row"><span>Policy version</span><strong>${esc(dash.policy?.version || "—")}</strong></div>
                <div class="cpd-row"><span>Last updated</span><strong>${esc(dash.consent?.lastUpdated ? new Date(dash.consent.lastUpdated).toLocaleString() : "—")}</strong></div>
                <div class="cpd-row"><span>Active since</span><strong>${esc(dash.consent?.lastUpdated ? new Date(dash.consent.lastUpdated).toLocaleDateString() : "—")}</strong></div>
              </div>
              <div class="cpd-card">
                <span class="cpd-pill">Privacy score</span>
                <p style="font-size:40px;font-weight:750;margin:12px 0 4px;letter-spacing:-.03em">${score}</p>
                <p class="cpd-muted">Based on granted purposes for the active policy.</p>
                <div class="cpd-row"><span>Open requests</span><strong>${dash.openRequestCount}</strong></div>
                <div class="cpd-row"><span>Unread notifications</span><strong>${dash.unreadNotificationCount}</strong></div>
              </div>
            </div>
            <div class="cpd-card">
              <strong>${esc(dash.policy?.title || dash.policy?.policyId || "Current policy")}</strong>
              <p class="cpd-muted" style="margin-top:6px">${esc(dash.policy?.notice || "No notice text.")}</p>
            </div>`;
        } else if (activeSection === "consent") {
          const purposes = dash.consent?.purposes ?? [];
          body.innerHTML = `
            <div class="cpd-card">
              <span class="cpd-pill">Current consent</span>
              <p class="cpd-muted" style="margin:8px 0 0">Read-only view of your active grants.</p>
              ${
                purposes.length === 0
                  ? `<p class="cpd-muted" style="margin-top:12px">No active consent.</p>`
                  : purposes
                      .map(
                        (p) =>
                          `<div class="cpd-row"><span>${esc(p.data_point_id)}</span><strong style="color:${p.consent_granted ? color : muted}">${p.consent_granted ? "Granted" : "Denied"}</strong></div>`,
                      )
                      .join("")
              }
            </div>`;
        } else {
          const purposes = dash.policy?.purposes ?? [];
          const grants = Object.fromEntries(
            (dash.consent?.purposes ?? []).map((p) => [p.data_point_id, p.consent_granted]),
          );
          body.innerHTML = `
            <div class="cpd-card">
              <span class="cpd-pill">Consent preferences</span>
              <p class="cpd-muted" style="margin:8px 0 12px">Toggle optional purposes. Required purposes stay on.</p>
              ${purposes
                .map((p) => {
                  const on = Boolean(p.required || grants[p.id]);
                  return `
                  <div class="cpd-row">
                    <span>
                      <strong>${esc(p.name)}</strong>
                      ${p.required ? ` <span class="cpd-pill">Necessary</span>` : ""}
                      <br/><span class="cpd-muted">${esc(p.description || p.id)}</span>
                    </span>
                    <button type="button" class="cpd-toggle ${on ? "on" : ""}" data-purpose="${esc(p.id)}" ${p.required ? "disabled" : ""} aria-pressed="${on}">
                      <span></span>
                    </button>
                  </div>`;
                })
                .join("")}
              <div class="cpd-actions">
                <button type="button" class="cpd-btn" data-cpd-save-prefs>Update preferences</button>
              </div>
            </div>`;
          body.querySelectorAll(".cpd-toggle:not([disabled])").forEach((btn) => {
            btn.addEventListener("click", () => {
              btn.classList.toggle("on");
              btn.setAttribute("aria-pressed", btn.classList.contains("on") ? "true" : "false");
            });
          });
          body.querySelector("[data-cpd-save-prefs]")?.addEventListener("click", async () => {
            if (!dash.policy) return;
            setError(null);
            const dataPointConsents = purposes.map((p) => {
              const el = body.querySelector(`[data-purpose="${p.id}"]`) as HTMLElement | null;
              return {
                data_point_id: p.id,
                consent_granted: p.required ? true : Boolean(el?.classList.contains("on")),
                purpose_agreed_to: p.name,
              };
            });
            try {
              await client.updatePreferences({
                policyId: dash.policy.policyId,
                policyVersion: dash.policy.version,
                dataPointConsents,
              });
              await refresh();
            } catch (e) {
              setError(e instanceof Error ? e.message : "Save failed");
            }
          });
        }
      } else if (activeSection === "history") {
        const hist = (await client.getConsentHistory()) as {
          items: Array<{ policyVersion: string; status: string; recordedAt: string; isActive: boolean }>;
        };
        body.innerHTML = `
          <div class="cpd-card">
            <span class="cpd-pill">Consent history</span>
            <p class="cpd-muted" style="margin:8px 0 12px">Read-only timeline of consent versions.</p>
            ${
              hist.items.length === 0
                ? `<p class="cpd-muted">No history yet.</p>`
                : hist.items
                    .map(
                      (i) => `
                  <div class="cpd-row">
                    <span>
                      <strong>Policy ${esc(i.policyVersion)}</strong> ${i.isActive ? '<span class="cpd-pill">Active</span>' : ""}
                      <br/><span class="cpd-muted">${esc(i.status)}</span>
                    </span>
                    <strong>${esc(new Date(i.recordedAt).toLocaleString())}</strong>
                  </div>`,
                    )
                    .join("")
            }
          </div>`;
      } else if (activeSection === "activity") {
        const act = (await client.getActivityTimeline()) as {
          items: Array<{ label: string; createdAt: string }>;
        };
        body.innerHTML = `
          <div class="cpd-card">
            <span class="cpd-pill">Privacy activity</span>
            ${
              act.items.length === 0
                ? `<p class="cpd-muted" style="margin-top:8px">No activity yet.</p>`
                : act.items
                    .map(
                      (i) =>
                        `<div class="cpd-row"><span>${esc(i.label)}</span><strong>${esc(new Date(i.createdAt).toLocaleString())}</strong></div>`,
                    )
                    .join("")
            }
          </div>`;
      } else if (activeSection === "requests") {
        const req = (await client.getRequests()) as {
          items: Array<{
            type: string;
            typeLabel?: string;
            requestNumber?: string;
            subject: string;
            statusLabel: string;
            submittedAt: string;
            updatedAt?: string;
          }>;
        };
        body.innerHTML = `
          <div class="cpd-card">
            <span class="cpd-pill">Requests</span>
            <p class="cpd-muted" style="margin:8px 0 12px">Submitted → Under review → In progress → Completed</p>
            ${
              req.items.length === 0
                ? `<p class="cpd-muted">No requests.</p>`
                : req.items
                    .map(
                      (i) => `
                  <div class="cpd-card" style="margin:0 0 10px;box-shadow:none">
                    <div class="cpd-row" style="border:0;padding:0">
                      <span><strong>${esc(i.typeLabel || i.type)}</strong><br/><span class="cpd-muted">${esc(i.requestNumber ? `${i.requestNumber} · ${i.subject}` : i.subject)}</span></span>
                      <span class="cpd-pill">${esc(i.statusLabel)}</span>
                    </div>
                    <div class="cpd-row"><span>Submitted</span><strong>${esc(new Date(i.submittedAt).toLocaleString())}</strong></div>
                    <div class="cpd-row"><span>Last updated</span><strong>${esc(i.updatedAt ? new Date(i.updatedAt).toLocaleString() : "—")}</strong></div>
                  </div>`,
                    )
                    .join("")
            }
          </div>`;
      } else if (activeSection === "notifications") {
        const notes = (await client.getPrivacyNotifications()) as {
          items: Array<{ id: string; title: string; body: string; createdAt: string; readAt: string | null }>;
        };
        body.innerHTML = `
          <div class="cpd-card">
            <div style="display:flex;justify-content:space-between;gap:8px;align-items:center">
              <span class="cpd-pill">Notifications</span>
              <button type="button" class="cpd-btn ghost" data-mark-all>Mark all read</button>
            </div>
            ${
              notes.items.length === 0
                ? `<p class="cpd-muted" style="margin-top:12px">No notifications.</p>`
                : notes.items
                    .map(
                      (n) => `
                  <div class="cpd-row">
                    <span><strong>${n.readAt ? "" : "● "}${esc(n.title)}</strong><br/><span class="cpd-muted">${esc(n.body)}</span></span>
                    <button type="button" class="cpd-btn ghost" data-read="${n.id}">${n.readAt ? "Read" : "Mark read"}</button>
                  </div>`,
                    )
                    .join("")
            }
          </div>`;
        body.querySelectorAll("[data-read]").forEach((btn) => {
          btn.addEventListener("click", async () => {
            const id = btn.getAttribute("data-read");
            if (!id) return;
            await client.markPrivacyNotificationRead(id);
            await refresh();
          });
        });
        body.querySelector("[data-mark-all]")?.addEventListener("click", async () => {
          for (const n of notes.items.filter((x) => !x.readAt)) {
            await client.markPrivacyNotificationRead(n.id);
          }
          await refresh();
        });
      } else if (activeSection === "actions") {
        body.innerHTML = `
          <div class="cpd-card">
            <span class="cpd-pill">Actions</span>
            <p class="cpd-muted" style="margin:8px 0 12px">Primary privacy actions. Your organisation is notified for every request.</p>
            <div class="cpd-actions">
              <button type="button" class="cpd-btn" data-act="prefs">Update preferences</button>
              <button type="button" class="cpd-btn ghost" data-act="withdraw">Withdraw consent</button>
              <button type="button" class="cpd-btn ghost" data-act="export">Request data export</button>
              <button type="button" class="cpd-btn ghost" data-act="correction">Request correction</button>
              <button type="button" class="cpd-btn ghost" data-act="complaint">Raise complaint</button>
              <button type="button" class="cpd-btn ghost" data-act="logout">Log out</button>
            </div>
            <div class="cpd-danger-zone">
              <strong style="color:#b91c1c">Danger zone</strong>
              <p class="cpd-muted" style="margin:6px 0 10px">Delete My Account creates a review ticket only. Your organisation deletes the account.</p>
              <button type="button" class="cpd-btn danger" data-act="delete">Delete my account</button>
            </div>
          </div>`;
        body.querySelector("[data-act=prefs]")?.addEventListener("click", () => {
          activeSection = "preferences";
          void refresh();
        });
        body.querySelector("[data-act=withdraw]")?.addEventListener("click", async () => {
          if (!confirm("Stop optional processing and withdraw consent?")) return;
          try {
            await client.withdrawConsent();
            await refresh();
          } catch (e) {
            setError(e instanceof Error ? e.message : "Withdraw failed");
          }
        });
        body.querySelector("[data-act=export]")?.addEventListener("click", async () => {
          try {
            await client.requestDataExport();
            activeSection = "requests";
            await refresh();
          } catch (e) {
            setError(e instanceof Error ? e.message : "Export request failed");
          }
        });
        body.querySelector("[data-act=correction]")?.addEventListener("click", async () => {
          const description = prompt("Describe the correction you need");
          if (!description) return;
          try {
            await client.requestCorrection({ description });
            activeSection = "requests";
            await refresh();
          } catch (e) {
            setError(e instanceof Error ? e.message : "Correction request failed");
          }
        });
        body.querySelector("[data-act=delete]")?.addEventListener("click", async () => {
          if (
            !confirm(
              "Submit a Delete Account request? Your organisation will review and delete your account. Aithoris only tracks the request.",
            )
          ) {
            return;
          }
          try {
            await client.requestAccountDeletion();
            activeSection = "requests";
            await refresh();
          } catch (e) {
            setError(e instanceof Error ? e.message : "Delete request failed");
          }
        });
        body.querySelector("[data-act=complaint]")?.addEventListener("click", async () => {
          const subject = prompt("Complaint subject");
          const description = prompt("Describe your complaint");
          if (!subject || !description) return;
          try {
            await client.raiseComplaint({ subject, description });
            activeSection = "requests";
            await refresh();
          } catch (e) {
            setError(e instanceof Error ? e.message : "Complaint failed");
          }
        });
        body.querySelector("[data-act=logout]")?.addEventListener("click", () => {
          client.logoutPrivacy();
          void refresh();
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
      if (String(e).includes("401") || String((e as Error).message).toLowerCase().includes("unauthorized")) {
        client.logoutPrivacy();
        renderLogin();
      }
    }
  };

  shell();
  void refresh();

  return {
    unmount: () => {
      destroyed = true;
      root.innerHTML = "";
    },
    refresh,
  };
}
